<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="driver-fleet-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Flota kierowców</h1>
      </div>
    </div>
    <v-tabs v-model="activeTab" class="mb-4 modern-tabs">
      <v-tab>{{ $t('driverFleet.active') }} ({{ totalItems }})</v-tab>
      <v-tab>{{ $t('driverFleet.pending') }} <v-badge v-if="pendingTotal > 0" :content="pendingTotal" color="orange" inline></v-badge></v-tab>
      <v-tab>{{ $t('driverFleet.archive') }} ({{ archiveTotal }})</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" touchless>
      <!-- ACTIVE -->
      <v-tab-item>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :options.sync="options"
          :server-items-length="totalItems"
          class="elevation-1"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-account-group</v-icon></div>
              <div class="filter-title">{{ $t('driverFleet.title') }}</div>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="search"
                :label="$t('common.search')"
                single-line
                hide-details
                clearable
                outlined
                dense
                class="mr-2"
                style="max-width:280px"
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="initialize()"
              />
            </div>
          </template>

          <template v-slot:item.sideNumber="{ item }">{{ item.sideNumber || '-' }}</template>
          <template v-slot:item.drivers="{ item }">
            <template v-if="item.driverEntries.length > 0">
              <v-chip
                v-for="d in item.driverEntries"
                :key="d.id + d.type"
                x-small
                class="mr-1 mb-1"
                :color="chipColor(d.type)"
                close
                close-icon="mdi-close-circle"
                @click:close="removeDriver(item, d)"
              >
                {{ d.name }}{{ chipSuffix(d.type) }}
              </v-chip>
            </template>
            <template v-else>-</template>
          </template>
          <template v-slot:item.licenses="{ item }">
            <template v-if="item.licenses.length > 0">
              <v-chip v-for="lic in item.licenses" :key="lic.id" x-small class="mr-1 mb-1">
                {{ lic.text }}{{ lic.licenseNo ? ' (' + lic.licenseNo + ')' : '' }}{{ lic.city ? ' - ' + lic.city : '' }}
              </v-chip>
            </template>
            <template v-else>-</template>
          </template>
          <template v-slot:item.odometer="{ item }">{{ item.odometer }} km</template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" @click="openAssignDialog(item)" :title="$t('driverFleet.assignDriver')">
              <v-icon small>mdi-account-plus-outline</v-icon>
            </v-btn>
            <v-btn icon small color="orange" @click="openEditDialog(item)" :title="$t('common.edit')">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small color="red" @click="confirmDelete(item)" :title="$t('common.delete')">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- PENDING -->
      <v-tab-item>
        <v-data-table
          :headers="pendingHeaders"
          :items="pendingItems"
          :loading="pendingLoading"
          :options.sync="pendingOptions"
          :server-items-length="pendingTotal"
          class="elevation-1"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-clock-outline</v-icon></div>
              <div class="filter-title">{{ $t('driverFleet.pendingApproval') }}</div>
            </div>
          </template>
          <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
          <template v-slot:item.actions="{ item }">
            <v-btn small color="success" class="mr-1" @click="approveCar(item)"><v-icon left small>mdi-check</v-icon>{{ $t('common.approve') }}</v-btn>
            <v-btn small color="error" @click="rejectCar(item)"><v-icon left small>mdi-close</v-icon>{{ $t('common.reject') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- ARCHIVE -->
      <v-tab-item>
        <v-data-table
          :headers="archiveHeaders"
          :items="archiveItems"
          :loading="archiveLoading"
          :options.sync="archiveOptions"
          :server-items-length="archiveTotal"
          class="elevation-1"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-archive-outline</v-icon></div>
              <div class="filter-title">{{ $t('driverFleet.archiveTitle') }}</div>
            </div>
          </template>
          <template v-slot:item.odometer="{ item }">{{ item.odometer }} km</template>
          <template v-slot:item.actions="{ item }">
            <v-btn small color="success" @click="confirmRestore(item)"><v-icon left small>mdi-archive-arrow-up</v-icon>{{ $t('driverFleet.restore') }}</v-btn>
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>

    <!-- Assign Driver Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="assignDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('driverFleet.assignDriverToVehicle') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('driverFleet.vehicle') }}: <b>{{ assignCarLabel }}</b></p>
          <v-autocomplete
            v-model="assignDriverId"
            :items="driversList"
            :loading="driversLoading"
            item-text="name"
            item-value="id"
            :label="$t('driverFleet.selectDriver')"
            outlined
            clearable
            :no-data-text="$t('driverFleet.driverNotFound')"
            :hint="$t('driverFleet.driverSearchHint')"
            persistent-hint
            prepend-inner-icon="mdi-magnify"
            :menu-props="{ maxHeight: 300 }"
          />
          <div v-if="!driversLoading" class="caption grey--text mt-1">
            {{ $t('driverFleet.driversInDb') }}: {{ driversList.length }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="assignDialog = false">{{ $t('driverFleet.skip') }}</v-btn>
          <v-btn color="primary" :disabled="!assignDriverId" @click="submitAssignDriver">{{ $t('driverFleet.assign') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ $t('driverFleet.editVehicle') }}</v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editFormValid">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="editForm.make" :label="$t('fleet.make') + ' *'" outlined dense :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="editForm.model" :label="$t('fleet.model') + ' *'" outlined dense :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.plate" :label="$t('fleet.registration') + ' *'" outlined dense :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.color" :label="$t('fleet.color') + ' *'" outlined dense :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="editForm.productionYear" :label="$t('fleet.productionYear') + ' *'" type="number" outlined dense :rules="[rules.required]" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model.number="editForm.odometer" :label="$t('fleet.odometer')" type="number" outlined dense />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.sideNumber" :label="$t('fleet.sideNumber')" outlined dense />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.insurancePolicyNumber" :label="$t('fleet.policyNumber')" outlined dense />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.insuranceValidTo" :label="$t('fleet.insuranceValidTo')" type="date" outlined dense />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.inspectionValidTo" :label="$t('fleet.inspectionValidTo')" type="date" outlined dense />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="editForm.lastService" :label="$t('fleet.lastService')" type="date" outlined dense />
              </v-col>
            </v-row>
          </v-form>

          <!-- Zdjęcia auta (wgrane przez kierowcę, do akceptacji) -->
          <v-divider class="my-3"></v-divider>
          <div class="d-flex align-center mb-2">
            <v-icon small left color="primary">mdi-camera</v-icon>
            <span class="subtitle-2">Zdjęcia auta</span>
            <v-chip v-if="carPhotosPendingCount > 0" x-small color="orange" dark class="ml-2">{{ carPhotosPendingCount }} do akceptacji</v-chip>
            <v-spacer></v-spacer>
            <v-progress-circular v-if="loadingCarPhotos" indeterminate size="16" width="2" color="primary"></v-progress-circular>
          </div>
          <div v-if="!carPhotos.length" class="caption grey--text mb-2">Kierowca nie dodał jeszcze zdjęć tego auta.</div>
          <v-row v-else dense>
            <v-col v-for="p in carPhotos" :key="p.id" cols="6" sm="4" md="3">
              <div class="dfp-photo-tile">
                <img v-if="carPhotoThumbs[p.fileId]" :src="carPhotoThumbs[p.fileId]" class="dfp-photo-img" @click="openCarPhoto(p)" />
                <div v-else class="dfp-photo-loading"><v-progress-circular indeterminate size="18" color="primary" /></div>
                <span class="dfp-photo-status" :class="'st-' + (p.status || '').toLowerCase()">{{ carPhotoStatusLabel(p.status) }}</span>
              </div>
              <div v-if="p.status === 'PENDING'" class="d-flex mt-1" style="gap:4px;">
                <v-btn x-small color="success" :loading="reviewingPhotoId === p.id" @click="reviewCarPhoto(p, 'ACCEPTED')">Akceptuj</v-btn>
                <v-btn x-small color="error" outlined :loading="reviewingPhotoId === p.id" @click="reviewCarPhoto(p, 'REJECTED')">Odrzuć</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!editFormValid" :loading="saving" @click="saveEdit">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");

export default {
  name: "DriverFleetPage",
  data() {
    return {
      activeTab: 0,
      loading: false,
      search: "",
      options: { page: 1, itemsPerPage: 25 },
      totalItems: 0,
      items: [],
      headers: [
        { text: "Marka", value: "make" },
        { text: "Model", value: "model" },
        { text: "Rejestracja", value: "plate" },
        { text: "Nr boczny", value: "sideNumber", sortable: false },
        { text: "Rok", value: "productionYear" },
        { text: "Przebieg", value: "odometer" },
        { text: "Kierowcy", value: "drivers", sortable: false },
        { text: "Licencje", value: "licenses", sortable: false },
        { text: "Akcje", value: "actions", sortable: false, width: "140px" },
      ],
      // Pending
      pendingItems: [], pendingLoading: false, pendingTotal: 0,
      pendingOptions: { page: 1, itemsPerPage: 25 },
      pendingHeaders: [
        { text: "Marka", value: "make" },
        { text: "Model", value: "model" },
        { text: "Rejestracja", value: "plate" },
        { text: "Rok", value: "year" },
        { text: "Dodał", value: "addedBy" },
        { text: "Data", value: "createdAt" },
        { text: "Akcje", value: "actions", sortable: false, width: "240px" },
      ],
      // Archive
      archiveItems: [], archiveLoading: false, archiveTotal: 0,
      archiveOptions: { page: 1, itemsPerPage: 25 },
      // Assign driver after restore
      assignDialog: false, assignCarId: null, assignCarLabel: "",
      assignDriverId: null, driversList: [],
      driversLoading: false,
      archiveHeaders: [
        { text: "Marka", value: "make" },
        { text: "Model", value: "model" },
        { text: "Rejestracja", value: "plate" },
        { text: "Rok", value: "productionYear" },
        { text: "Przebieg", value: "odometer" },
        { text: "Akcje", value: "actions", sortable: false, width: "140px" },
      ],
      // Edit
      editDialog: false, editFormValid: false, editId: null, saving: false,
      // Zdjęcia auta (wgrane przez kierowcę, do akceptacji)
      carPhotos: [], carPhotoThumbs: {}, loadingCarPhotos: false, reviewingPhotoId: null,
      editForm: {
        make: "", model: "", plate: "", color: "", productionYear: null,
        odometer: 0, sideNumber: "", insurancePolicyNumber: "",
        insuranceValidTo: "", inspectionValidTo: "", lastService: "",
      },
      rules: { required: (v) => !!v || "Wymagane" },
    };
  },
  watch: {
    options: { handler() { this.initialize(); }, deep: true },
    pendingOptions: { handler() { this.fetchPending(); }, deep: true },
    archiveOptions: { handler() { this.fetchArchive(); }, deep: true },
    activeTab(v) { if (v === 1) this.fetchPending(); if (v === 2) this.fetchArchive(); },
    search() { this.options.page = 1; this.initialize(); },
  },
  async created() {
    await this.initialize();
    await this.fetchPendingCount();
  },
  computed: {
    carPhotosPendingCount() {
      return (this.carPhotos || []).filter((p) => p.status === 'PENDING').length;
    },
  },
  methods: {
    // ─── Zdjęcia auta ───
    async loadCarPhotos(carId) {
      this.carPhotos = [];
      this.carPhotoThumbs = {};
      if (!carId) return;
      this.loadingCarPhotos = true;
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + "car-photos/car/" + carId, { headers: this.getHeaders() });
        this.carPhotos = (res.data && res.data.data) || [];
        this.carPhotos.forEach((p) => this.loadCarPhotoThumb(p.fileId));
      } catch (e) {
        this.carPhotos = [];
      }
      this.loadingCarPhotos = false;
    },
    async loadCarPhotoThumb(fileId) {
      if (!fileId || this.carPhotoThumbs[fileId]) return;
      try {
        const resp = await fetch(process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId, {
          method: "POST",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        if (!resp.ok) return;
        const blob = await resp.blob();
        this.$set(this.carPhotoThumbs, fileId, window.URL.createObjectURL(blob));
      } catch (e) { /* ignore */ }
    },
    async reviewCarPhoto(photo, status) {
      this.reviewingPhotoId = photo.id;
      try {
        let note = undefined;
        if (status === 'REJECTED') {
          const r = await Swal.fire({ title: 'Powód odrzucenia (opcjonalnie)', input: 'text', showCancelButton: true, confirmButtonText: 'Odrzuć', cancelButtonText: 'Anuluj' });
          if (!r.isConfirmed) { this.reviewingPhotoId = null; return; }
          note = r.value || '';
        }
        await axios.post(process.env.VUE_APP_ROOT_API + "car-photos/" + photo.id + "/review", { status, note }, { headers: this.getHeaders() });
        photo.status = status;
        photo.reviewNote = note || photo.reviewNote;
      } catch (e) {
        Swal.fire("Błąd", (e.response && e.response.data && e.response.data.message) || "Nie udało się zapisać", "error");
      }
      this.reviewingPhotoId = null;
    },
    carPhotoStatusLabel(status) {
      return ({ PENDING: 'Oczekuje', ACCEPTED: 'Zaakceptowane', REJECTED: 'Odrzucone' })[status] || status;
    },
    openCarPhoto(p) {
      const url = this.carPhotoThumbs[p.fileId];
      if (url) window.open(url, '_blank');
    },
    getHeaders() {
      return { Authorization: "Bearer " + localStorage.getItem("token") };
    },
    async initialize() {
      this.loading = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars/driver-fleet?take=${this.options.itemsPerPage}&skip=${this.options.page}&search=${this.search || ""}`,
          headers: this.getHeaders(),
        });
        this.items = response.data.data.result.map((car) => {
          const driverEntries = [
            ...(car.drivers || []).map((d) => ({ id: d.id, name: `${d.firstName} ${d.lastName}`, type: 'primary' })),
            ...(car.secondDrivers || []).map((d) => ({ id: d.id, name: `${d.firstName} ${d.lastName}`, type: 'second' })),
            ...(car.additionalDrivers || []).map((d) => ({ id: d.id, name: `${d.firstName} ${d.lastName}`, type: 'additional' })),
          ];
          return {
            id: car.id, make: car.make, model: car.model, plate: car.plate,
            sideNumber: car.sideNumber, productionYear: car.productionYear, odometer: car.odometer,
            driverEntries,
            licenses: (car.licenses || []).map((l) => ({ id: l.id, text: l.text, licenseNo: l.licenseNo, city: l.city })),
          };
        });
        this.totalItems = response.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.loading = false;
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString("pl-PL") : "-"; },
    async fetchPendingCount() {
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "cars/pending-approval?take=1&skip=1", headers: this.getHeaders() });
        this.pendingTotal = res.data.data?.count || 0;
      } catch (e) { console.error(e); }
    },
    async fetchPending() {
      this.pendingLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars/pending-approval?take=${this.pendingOptions.itemsPerPage}&skip=${this.pendingOptions.page}`,
          headers: this.getHeaders(),
        });
        this.pendingItems = res.data.data.result || [];
        this.pendingTotal = res.data.data.count || 0;
      } catch (e) { console.error(e); }
      this.pendingLoading = false;
    },
    async approveCar(item) {
      const r = await Swal.fire({
        title: "Zatwierdzić pojazd?",
        html: `<b>${item.make} ${item.model}</b> (${item.plate})<br>Dodany przez: ${item.addedBy}`,
        icon: "question", showCancelButton: true, confirmButtonText: "Zatwierdź", cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "cars/" + item.id + "/approve", headers: this.getHeaders() });
        Swal.fire("Zatwierdzono", "Pojazd dodany do floty", "success");
        this.fetchPending(); this.fetchPendingCount(); this.initialize();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error"); }
    },
    async rejectCar(item) {
      const r = await Swal.fire({
        title: "Odrzucić pojazd?",
        html: `<b>${item.make} ${item.model}</b> (${item.plate})`,
        icon: "warning", input: "textarea", inputPlaceholder: "Powód odrzucenia...",
        showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Odrzuć", cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "cars/" + item.id + "/reject", headers: this.getHeaders(), data: { reason: r.value || '' } });
        Swal.fire("Odrzucono", "Pojazd odrzucony", "info");
        this.fetchPending(); this.fetchPendingCount();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error"); }
    },
    async fetchArchive() {
      this.archiveLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars/driver-fleet/archive?take=${this.archiveOptions.itemsPerPage}&skip=${this.archiveOptions.page}`,
          headers: this.getHeaders(),
        });
        this.archiveItems = (res.data.data.result || []).map((car) => ({
          id: car.id, make: car.make, model: car.model, plate: car.plate,
          productionYear: car.productionYear, odometer: car.odometer,
        }));
        this.archiveTotal = res.data.data.count;
      } catch (e) { console.error(e); }
      this.archiveLoading = false;
    },
    async confirmRestore(item) {
      const r = await Swal.fire({
        title: "Przywrócić pojazd?",
        html: `<b>${item.make} ${item.model}</b> (${item.plate})`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Przywróć",
        cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars/driver-fleet/archive/" + item.id + "/restore",
          headers: this.getHeaders(),
        });
        this.fetchArchive();
        this.initialize();
        // Open assign driver dialog
        this.assignCarId = item.id;
        this.assignCarLabel = `${item.make} ${item.model} (${item.plate})`;
        this.assignDriverId = null;
        await this.fetchDriversList();
        this.assignDialog = true;
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async fetchDriversList() {
      this.driversLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "drivers?take=10000&skip=1",
          headers: this.getHeaders(),
        });
        const data = res.data.data?.result || res.data.data || [];
        this.driversList = data.map((d) => {
          const email = d.email || "";
          return {
            id: d.id,
            firstName: d.firstName || "",
            lastName: d.lastName || "",
            email,
            phone: d.phone || "",
            name: `${d.firstName} ${d.lastName}${email ? " (" + email + ")" : ""}`,
          };
        });
      } catch (e) {
        console.error(e);
      }
      this.driversLoading = false;
    },
    chipColor(type) {
      if (type === 'second') return '#7E57C2';
      if (type === 'additional') return '#26A69A';
      return undefined;
    },
    chipSuffix(type) {
      if (type === 'second') return ' (2)';
      if (type === 'additional') return ' (dod.)';
      return '';
    },
    async removeDriver(car, driver) {
      const r = await Swal.fire({
        title: "Odpiąć kierowcę?",
        html: `<b>${driver.name}</b> od pojazdu <b>${car.make} ${car.model}</b> (${car.plate})`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Odepnij",
        cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars/" + car.id + "/unassign-driver",
          headers: this.getHeaders(),
          data: { driverId: driver.id },
        });
        await this.initialize();
      } catch (e) {
        const msg = e.response?.data?.message?.toString() || e.message || "Wystąpił błąd";
        Swal.fire("Błąd", msg, "error");
      }
    },
    async openAssignDialog(item) {
      this.assignCarId = item.id;
      this.assignCarLabel = `${item.make} ${item.model} (${item.plate})`;
      this.assignDriverId = null;
      await this.fetchDriversList();
      this.assignDialog = true;
    },
    async submitAssignDriver() {
      if (!this.assignDriverId || !this.assignCarId) return;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars/" + this.assignCarId + "/assign-driver",
          headers: this.getHeaders(),
          data: { driverId: this.assignDriverId },
        });
        Swal.fire("Przypisano", "Kierowca przypisany do pojazdu", "success");
        this.assignDialog = false;
        await this.initialize();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async openEditDialog(item) {
      try {
        const res = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "cars/" + item.id, headers: this.getHeaders() });
        const car = res.data.data;
        this.editId = car.id;
        this.editForm = {
          make: car.make || "", model: car.model || "", plate: car.plate || "", color: car.color || "",
          productionYear: car.productionYear || null, odometer: car.odometer || 0, sideNumber: car.sideNumber || "",
          insurancePolicyNumber: car.insurancePolicyNumber || "",
          insuranceValidTo: car.insuranceValidTo ? car.insuranceValidTo.substring(0, 10) : "",
          inspectionValidTo: car.inspectionValidTo ? car.inspectionValidTo.substring(0, 10) : "",
          lastService: car.lastService ? car.lastService.substring(0, 10) : "",
        };
        this.editDialog = true;
        this.loadCarPhotos(car.id);
      } catch (e) { Swal.fire("Błąd", "Nie udało się pobrać danych", "error"); }
    },
    async saveEdit() {
      if (!this.$refs.editForm.validate()) return;
      this.saving = true;
      try {
        await axios({
          method: "post", url: process.env.VUE_APP_ROOT_API + "cars", headers: this.getHeaders(),
          data: {
            id: this.editId, make: this.editForm.make, model: this.editForm.model, plate: this.editForm.plate,
            color: this.editForm.color, productionYear: parseInt(this.editForm.productionYear),
            odometer: parseInt(this.editForm.odometer) || 0, previousServiceOdometer: 0,
            sideNumber: this.editForm.sideNumber || null, insurancePolicyNumber: this.editForm.insurancePolicyNumber || null,
            insuranceValidTo: this.editForm.insuranceValidTo || new Date().toISOString(),
            inspectionValidTo: this.editForm.inspectionValidTo || new Date().toISOString(),
            lastService: this.editForm.lastService || new Date().toISOString(),
            isCompanyCar: false, wrapType: "NONE", advance: 0, rentPrice: 0,
          },
        });
        Swal.fire("Zapisano", "Pojazd zaktualizowany", "success");
        this.editDialog = false;
        await this.initialize();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error"); }
      this.saving = false;
    },
    async confirmDelete(item) {
      const r = await Swal.fire({
        title: "Usunąć pojazd?",
        html: `<b>${item.make} ${item.model}</b> (${item.plate})<br><small>Pojazd trafi do archiwum.</small>`,
        icon: "warning", showCancelButton: true, confirmButtonColor: "#d33",
        confirmButtonText: "Usuń", cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({ method: "delete", url: process.env.VUE_APP_ROOT_API + "cars/" + item.id, headers: this.getHeaders() });
        Swal.fire("Usunięto", "Pojazd przeniesiony do archiwum", "success");
        await this.initialize();
      } catch (e) { Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error"); }
    },
  },
};
</script>

<style scoped>
/* Zdjęcia auta — kafelki */
.dfp-photo-tile {
  position: relative;
  width: 100%;
  padding-top: 72%;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255,255,255,0.06);
  cursor: pointer;
}
.dfp-photo-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.dfp-photo-loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.dfp-photo-status {
  position: absolute; left: 5px; bottom: 5px;
  font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 999px;
  color: #fff; text-transform: uppercase; letter-spacing: 0.03em;
}
.dfp-photo-status.st-pending { background: #F59E0B; }
.dfp-photo-status.st-accepted { background: #2DCB73; }
.dfp-photo-status.st-rejected { background: #EF5350; }

.driver-fleet-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.driver-fleet-page >>> .v-tabs-bar {
  background: transparent !important;
}
.driver-fleet-page >>> .v-tab {
  color: #94A0B8 !important;
}
.driver-fleet-page >>> .v-tab--active {
  color: #5478FF !important;
}
.driver-fleet-page >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.driver-fleet-page >>> .v-tabs-items {
  background: transparent !important;
}
.driver-fleet-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.driver-fleet-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.driver-fleet-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.driver-fleet-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.driver-fleet-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.driver-fleet-page >>> .v-text-field input,
.driver-fleet-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.driver-fleet-page >>> .v-label {
  color: #94A0B8 !important;
}
.driver-fleet-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.driver-fleet-page >>> .v-card__title,
.driver-fleet-page >>> .v-card__text {
  color: #E6EAF2 !important;
}
</style>
